import { useCallback, useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { IBubblePosition, ITooltipProps } from '../../../interfaces/globals';
import type { TooltipPlacement } from '../../../types/globals';

import './Tooltip.scss';

const getAnchor = (rect: DOMRect, placement: TooltipPlacement): IBubblePosition => {
  switch (placement) {
    case 'top':
      return { top: rect.top, left: rect.left + rect.width / 2 };
    case 'bottom':
      return { top: rect.bottom, left: rect.left + rect.width / 2 };
    case 'left':
      return { top: rect.top + rect.height / 2, left: rect.left };
    case 'right':
      return { top: rect.top + rect.height / 2, left: rect.right };
    default:
      return { top: rect.top, left: rect.left + rect.width / 2 };
  }
};

const Tooltip = ({
  content,
  children,
  placement = 'top',
  disabled = false,
  triggerClassName = 'tooltip-trigger',
  inline = false
}: ITooltipProps) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const readyRef = useRef(false);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<IBubblePosition>({ top: 0, left: 0 });

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      readyRef.current = true;
    });
    return () => {
      cancelAnimationFrame(raf);
      readyRef.current = false;
    };
  }, []);

  const show = useCallback(() => {
    if (!readyRef.current || !triggerRef.current) return;
    setPosition(getAnchor(triggerRef.current.getBoundingClientRect(), placement));
    setVisible(true);
  }, [placement]);

  const hide = useCallback(() => setVisible(false), []);

  if (disabled) return <>{children}</>;

  const bubble = visible && (
    <div
      className={`tooltip-bubble tooltip-bubble--${placement}`}
      style={{ top: position.top, left: position.left }}
      role="tooltip"
    >
      {content}
    </div>
  );

  return (
    <div
      ref={triggerRef}
      className={triggerClassName}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {inline ? bubble : createPortal(bubble, document.body)}
    </div>
  );
};

export default Tooltip;
